import { Input, ModuleWrapper } from '@/components'
import { LeaderGroupCombobox } from '@/components/combobox'
import { Button as BaseButton } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { PlainLink } from '@/components_deprecated/Link'
import { PermissionsRequired } from '@/containers/permission-containers'
import { PageType } from '@/modules/common/consts'
import { Module } from '@/modules/companies'
import { asEnumValue, asNullableString, asString } from '@/utils'
import {
  useAdmin,
  useAutoSelectSingleLeaderGroup,
  useDebounce,
  useLeaderGroups,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import React, { useMemo } from 'react'
import { BrowserView, MobileView } from 'react-device-detect'
import styled from 'styled-components'
import type { Tab } from '../../../components_deprecated/Tabs'
import { TabPane, Tabs } from '../../../components_deprecated/Tabs'
import { ThreadsTable } from '../components'
import { ForumTabKey } from '../types'

type ForumTabKeyValue = (typeof ForumTabKey)[keyof typeof ForumTabKey]
const forumTabValues = [ForumTabKey.GENERAL, ForumTabKey.DIRECT] as const

const Controls = styled(Card)`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-flow: column;
  gap: 1rem;

  margin-bottom: 1rem;
  padding: 1rem;
`
const Button = styled(BaseButton)`
  ${props => props.theme.media.mobile} {
    padding: 0 3rem 0 1rem;
  }
`

export const Forum: React.FC = () => {
  const translations = useTranslate({
    forum: 'common.forum',
    searchInForum: 'forum.search',
    newThread: 'forum.new-thread',
    chooseGroup: 'common.choose-group',

    general: 'common.general',
    direct: 'common.direct',
  })

  const tabs = useMemo<[Tab, Tab]>(
    () => [
      { key: ForumTabKey.GENERAL, text: translations.general },
      { key: ForumTabKey.DIRECT, text: translations.direct },
    ],
    [translations.general, translations.direct]
  )

  const { formValues: filter, updateForm, loadStoredFormState } = useForm<{
    generalQuery: string
    directQuery: string
    generalSort: string
    directSort: string
    activeTabKey: ForumTabKeyValue
    selectedLeaderGroup: string | null
  }>({
    values: {
      generalQuery: '',
      directQuery: '',
      generalSort: '-createdAt',
      directSort: '-createdAt',
      activeTabKey: ForumTabKey.GENERAL,
      selectedLeaderGroup: null,
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'forum-filters',
        sessionStorage: true,
        transformValues: {
          generalQuery: value => asString(value) ?? '',
          directQuery: value => asString(value) ?? '',
          generalSort: value => asString(value) || '-createdAt',
          directSort: value => asString(value) || '-createdAt',
          activeTabKey: value =>
            asEnumValue(value, forumTabValues) ?? ForumTabKey.GENERAL,
          selectedLeaderGroup: value => {
            const parsed = asNullableString(value)
            return parsed === '' ? null : parsed
          },
        },
      },
    },
  })

  const debouncedGeneralQuery = useDebounce(filter.generalQuery)
  const debouncedDirectQuery = useDebounce(filter.directQuery)

  const isAdmin = useAdmin()
  const { hasAccess, myLeaderGroups, groupsLoading } = useLeaderGroups(
    Module.FORUM
  )

  useAutoSelectSingleLeaderGroup({
    canViewOthers: hasAccess && !groupsLoading,
    isAdmin,
    leaderGroup: filter.selectedLeaderGroup ?? undefined,
    leaderGroups: myLeaderGroups,
    loadStoredFormState,
    updateForm,
    onAutoSelect: group => {
      updateForm({ selectedLeaderGroup: group.id })
    },
  })

  const activeTab = useMemo<Tab>(
    () =>
      tabs.find(tab => tab.key === filter.activeTabKey) ??
      tabs[0] ?? { key: ForumTabKey.GENERAL, text: translations.general },
    [filter.activeTabKey, tabs, translations.general]
  )

  const createLink =
    filter.activeTabKey === ForumTabKey.GENERAL
      ? '/forum/create'
      : '/forum/create-direct'

  return (
    <ModuleWrapper
      title={translations.forum}
      page={PageType.FORUM}
      headerRight={
        <MobileView>
          <PermissionsRequired permissions={PERMISSIONS.forum.add.thread}>
            <PlainLink to={createLink}>
              <Button
                iconLeftProps={{ icon: 'plus', margin: '0' }}
                height="42px"
                padding="0"
                fontSize="0.8em"
              >
                {translations.newThread}
              </Button>
            </PlainLink>
          </PermissionsRequired>
        </MobileView>
      }
    >
      <Tabs
        activeTab={activeTab}
        tabs={tabs}
        onChange={tab =>
          updateForm({
            activeTabKey:
              tab.key === ForumTabKey.DIRECT
                ? ForumTabKey.DIRECT
                : ForumTabKey.GENERAL,
          })
        }
      />

      <TabPane active={activeTab.key === ForumTabKey.GENERAL}>
        <Controls>
          <Input
            value={filter.generalQuery}
            type="search"
            placeholder={translations.searchInForum}
            onChange={value => updateForm({ generalQuery: value })}
          />

          <BrowserView>
            <PermissionsRequired permissions={PERMISSIONS.forum.add.thread}>
              <PlainLink to="/forum/create">
                <Button iconLeftProps={{ icon: 'plus' }}>
                  {translations.newThread}
                </Button>
              </PlainLink>
            </PermissionsRequired>
          </BrowserView>
        </Controls>

        <ThreadsTable
          query={debouncedGeneralQuery}
          sort={filter.generalSort}
          onSort={value => updateForm({ generalSort: value })}
          leaderGroupId={null}
        />
      </TabPane>

      <TabPane active={activeTab.key === ForumTabKey.DIRECT}>
        <Controls>
          <Input
            value={filter.directQuery}
            type="search"
            placeholder={translations.searchInForum}
            onChange={value => updateForm({ directQuery: value })}
          />

          {hasAccess && (
            <LeaderGroupCombobox
              mode="single"
              value={filter.selectedLeaderGroup ?? ''}
              clearable
              module={Module.FORUM}
              onChange={value =>
                updateForm({ selectedLeaderGroup: value || null })
              }
              placeholder={translations.chooseGroup}
            />
          )}

          <BrowserView>
            <PermissionsRequired permissions={PERMISSIONS.forum.add.thread}>
              <PlainLink to="/forum/create-direct">
                <Button iconLeftProps={{ icon: 'plus' }}>
                  {translations.newThread}
                </Button>
              </PlainLink>
            </PermissionsRequired>
          </BrowserView>
        </Controls>

        <ThreadsTable
          query={debouncedDirectQuery}
          sort={filter.directSort}
          onSort={value => updateForm({ directSort: value })}
          isDirectMessage
          leaderGroupId={filter.selectedLeaderGroup}
        />
      </TabPane>
    </ModuleWrapper>
  )
}
